import { Injectable, signal, WritableSignal } from '@angular/core';
import { Role } from './role.enum';
import { UserPermissionsResponse } from '../dto/user-permissions.dto';

@Injectable({
  providedIn: 'root',
})
export class PermissionService<T extends string = string> {
  private readonly permissions: WritableSignal<Set<string>> = signal<Set<string>>(new Set());
  private readonly roles: WritableSignal<Set<string>> = signal<Set<string>>(new Set());

  setPermissionsFromApi(response: UserPermissionsResponse): void {
    this.permissions.set(new Set(response.permissions));
    this.roles.set(new Set(response.roles));
  }

  setPermissions(permissions: T[]): void {
    this.permissions.set(new Set<string>(permissions));
  }

  clearPermissions(): void {
    this.permissions.set(new Set());
    this.roles.set(new Set());
  }

  can(permission: T): boolean {
    const currentPermissions: Set<string> = this.permissions();

    if (currentPermissions.has('*')) {
      return true;
    }

    if (currentPermissions.has(permission)) {
      return true;
    }

    const action: string = permission.split('.').pop() ?? '';
    const wildcardForAction: string = `*.${action}`;

    return currentPermissions.has(wildcardForAction);
  }

  canAny(permissions: T[]): boolean {
    for (const permission of permissions) {
      if (this.can(permission)) {
        return true;
      }
    }

    return false;
  }

  canAll(permissions: T[]): boolean {
    for (const permission of permissions) {
      if (!this.can(permission)) {
        return false;
      }
    }

    return true;
  }

  hasRole(role: string): boolean {
    return this.roles().has(role);
  }

  getRoles(): Set<string> {
    return this.roles();
  }

  getPermissions(): Set<string> {
    return this.permissions();
  }

  isRestricted(): boolean {
    const roles: Set<string> = this.roles();
    const isOnlyCustomer: boolean = roles.size === 1 && roles.has(Role.CUSTOMER);

    return roles.size === 0 || isOnlyCustomer;
  }
}
